import {Power1, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {Mental} from "../../Mental";
import {MentalHelper} from "../../utils/MentalHelper";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BasicBitmapCountUp} from "../countUp/BasicBitmapCountUp";
import {IMentalGameData} from "../../server/MentalData";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export interface IPos {
    x: {
        landscape: number,
        portrait: number
    };
    y: number;
}

export class BigWin extends PIXI.Container implements StageLayer {
    public _bigWinSpine: PIXI.spine.Spine;
    private _level: number = 0;
    private _positions: IPos[] = [
        {
            x: {
                landscape: 565,
                portrait: 510
            }, y: 360
        },
        {
            x: {
                landscape: 420,
                portrait: 420
            }
            , y: 450
        },
        {
            x: {
                landscape: 495,
                portrait: 455
            }, y: 450
        },
        {
            x: {
                landscape: 495,
                portrait: 455
            }, y: 440
        },
        {
            x: {
                landscape: 480,
                portrait: 440
            }, y: 440
        }
    ];
    private _bigWinCountUp: BasicBitmapCountUp;
    private _fromCount: number = 0;
    private _toCount: number = 0;

    private _bigWinLevels: PIXI.spine.Spine[] = [];
    private _bigWinCircle: PIXI.spine.Spine;
    private _oldFilm: PIXI.spine.Spine;
    private _blast: TimelineSprite;
    private _transitionYTween: TweenLite;
    private _overlay: PIXI.Graphics;

    // #365
    private _bigWinCountUpTl: TimelineLite;
    private _bigWinCountUpEndTween: TweenLite;

    constructor() {
        super();

        this.init();
    }

    public isBigWinTriggered(): boolean {
        return Mental.model.gameData.isBigWin;
    }

    public playBigWinLevelAnimation(level: number): TimelineMax {
        const gameData: IMentalGameData = Mental.model.gameData,
            levelMoreThan1: boolean = level > 1,
            levelAnimation: TimelineMax = MentalHelper.timeLineKiller(new TimelineMax(), new TimelineMax({
                paused: true
            })),
            boardAnimName: string = `level${level}_animation`,
            characterAnimName: string = `level${level}_skull_convert_animation`;

        this._fromCount = 0;
        this._level = level;

        if (level === 1) {
            this.onResize();

            this._bigWinCircle.visible = true;
            this._bigWinCircle.updateTransform();
            this.resetSpineItem(this._bigWinCircle);
            this._bigWinCircle.state.setAnimation(0, "animation", true);

            this._oldFilm.visible = true;
            this._oldFilm.updateTransform();
            this.resetSpineItem(this._oldFilm);
            this._oldFilm.state.setAnimation(0, "old_film_effect_animation", true);

            this._bigWinCountUp.setText("");
        }

        if (levelMoreThan1) {
            this._bigWinCountUp.stopAt(this._toCount);
            this._fromCount = Mental.WIN_RATIOS.bigWin[level - 2].ratio * Mental.model.gameData.playedBetValue;

            const nextLevel: PIXI.spine.Spine = this._bigWinLevels[level - 1];
            this.resetSpineItem(nextLevel);
            nextLevel.y = (NolimitApplication.screenBounds.top - 450);
            this._transitionYTween = TweenLite.to(nextLevel, {
                duration: 0.1666,
                y: this._positions[level - 1].y,
                onComplete: () => {
                    this._bigWinSpine.visible = false;
                    this._bigWinSpine.state.clearTracks();
                }
            });
            levelAnimation.add(() => {this._blast.getAnimationAutoShowHide(true, true)}, 0);
            levelAnimation.add(this._transitionYTween, 0);

            // #347
            levelAnimation.to(this._bigWinCountUp.scale, {
                duration: 0.15,
                x: 1.3,
                y: 1.3
            });
            levelAnimation.to(this._bigWinCountUp.scale, {
                duration: 0.15,
                x: 1,
                y: 1
            });
        }

        levelAnimation.add([
            () => {
                this._bigWinSpine = this._bigWinLevels[level - 1];
                this.resetSpineItem(this._bigWinSpine);
                this._bigWinSpine.position.set(((NolimitApplication.isLandscape) ? (this._positions[this._level - 1].x.landscape) : (this._positions[this._level - 1].x.portrait)), this._positions[this._level - 1].y);
                this._bigWinSpine.state.setAnimation(0, boardAnimName, true);

                // last level
                if (gameData.currentWinRatioConfig.ratio === Mental.WIN_RATIOS.bigWin[level - 1].ratio) {
                    this._toCount = gameData.totalBetWayWinnings;
                }
                // other levels
                else {
                    this._toCount = Mental.WIN_RATIOS.bigWin[level - 1].ratio * gameData.playedBetValue;
                }
            },
            this.playCountUp(level) // tl time shall be same as count up time
        ]);
        levelAnimation.add(() => {this._bigWinSpine.state.setAnimation(1, characterAnimName, false)}, (levelMoreThan1 ? 0.2 + 0.1666 + 0.3 : 0.2));

        return levelAnimation.play(0);
    }

    public playEndingAnimation(): TimelineMax {
        const endingTl: TimelineMax = new TimelineMax();
        endingTl.add([
            TweenLite.fromTo([this._bigWinSpine, this._bigWinCircle, this._oldFilm], {alpha: 1}, {
                duration: 0.5, alpha: 0,
                onStart: () => {
                    if (Mental.model.gameData.brokeTheBank) {
                        this._overlay.visible = true;
                    }
                }
            }),
            this.stopCountUp(false),
            () => Mental.winWays.showTotalWinWaysItems(true)
        ]);
        endingTl.eventCallback("onComplete", () => {
            this._bigWinSpine.visible = false;
            this._bigWinSpine.state.clearTracks();
            // #335 => fall safe, making all disappear, even though, the last assigned is clear above.
            for (let i: number = 0; i < this._bigWinLevels.length; i++) {
                this._bigWinLevels[i].visible = false;
                this._bigWinLevels[i].state.clearTracks();
            }

            this._bigWinCircle.visible = false;
            this._bigWinCircle.state.clearTracks();

            this._oldFilm.visible = false;
            this._oldFilm.state.clearTracks();

            if (this._transitionYTween) {
                this._transitionYTween.pause();
                this._transitionYTween.kill();
            }
        });

        return endingTl;
    }

    // #365
    public onAbort(): void {
        Mental.winWays.onAbort();

        if (this._bigWinCountUpEndTween) {
            this._bigWinCountUpEndTween.pause();
            this._bigWinCountUpEndTween.kill();

            Mental.winWays.showTotalWinWaysItems(true);

            this.stopCountUp(true);
        }
    }

    // #365
    public hideCountUp(): void {
        if (this._bigWinCountUp.visible) {
            this._bigWinCountUp.visible = false;
        }
    }

    public onResize(): void {
        const isLandscape: boolean = NolimitApplication.isLandscape,
            scale: number = (isLandscape ? 1.5 : 1),
            circleScale: number = (isLandscape ? 1.1 : 0.73334),
            oldFilmScale: number = (isLandscape ? 1 : 0.66667);

        this._bigWinCountUp.y = isLandscape ? 365 : 390;

        for (let i: number = 0; i < this._bigWinLevels.length; i++) {
            this._bigWinLevels[i].scale.set(scale);

            this._bigWinLevels[i].x = isLandscape ? this._positions[i].x.landscape : this._positions[i].x.portrait;
        }

        this._bigWinCircle.scale.set(circleScale);
        this._oldFilm.scale.set(oldFilmScale);

        if (this._transitionYTween && this._transitionYTween.totalProgress() > 0 && this._transitionYTween.totalProgress() < 1) {
            this._transitionYTween.pause();
            this._transitionYTween.kill();

            this._bigWinLevels[this._level - 1].y = this._positions[this._level - 1].y;
        }
    }

    public hideOverlay(): void {
        this._overlay.visible = false;
    }

    private init(): void {
        this.createOverlayForWinCap();
        this.createBigWinLevels();

        this._bigWinCircle = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("bigWinCircle"));
        this._bigWinCircle.visible = false;
        this._bigWinCircle.position.set(380, 360);

        this._oldFilm = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("bigWinOldFilm"));
        this._oldFilm.visible = false;
        this._oldFilm.position.set(360);

        this._bigWinCountUp = new BasicBitmapCountUp({
            fontName: "MentalNumbersWinCountUp",
            fontSize: 35,
            tint: 0xffffff
        }, 710, new PIXI.Point(0.5));
        this._bigWinCountUp.position.set(360, 0);
        this._bigWinCountUp.visible = false;

        this._blast = new TimelineSprite(Mental.assetLoader.getTextures("bigWinBlast"));
        this._blast.hide();
        this._blast.blendMode = PIXI.BLEND_MODES.ADD;
        this._blast.anchor.set(0.5);
        this._blast.position.set(360);
        this._blast.scale.set(2);

        this.addChild(this._bigWinCircle, this._oldFilm, this._bigWinCountUp, this._blast);
    }

    private createBigWinLevels(): void {
        const totalLevels: number = 5;

        for (let i: number = 0; i < totalLevels; i++) {
            let item: PIXI.spine.Spine = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("bigWinLevel" + (i + 1)));
            item.visible = false;

            this.addChild(item);
            this._bigWinLevels.push(item);
        }
    }

    private createOverlayForWinCap(): void {
        this._overlay = new PIXI.Graphics();
        this._overlay.visible = false;
        this._overlay.beginFill(0x000000);
        this._overlay.drawRect(-360, -360, 1680, 1680);
        this._overlay.endFill();
        this._overlay.visible = false;
        this.addChild(this._overlay);
    }

    private resetSpineItem(item: PIXI.spine.Spine): void {
        item.skeleton.setSlotsToSetupPose();
        item.skeleton.setToSetupPose();
        item.state.clearTracks();
        item.state.setEmptyAnimation(0, 0);

        if (!item.visible) {
            item.visible = true;
            item.updateTransform();
        }

        item.alpha = 1;
    }

    private playCountUp(level: number): TimelineMax {
        const duration: number = Mental.WIN_RATIOS.bigWin[level - 1].duration,
            tl: TimelineMax = new TimelineMax();

        // no need to add time in the tl.
        tl.add(() => {
            this._bigWinCountUp.visible = true;
            this._bigWinCountUp.alpha = 1;
            this._bigWinCountUpTl = this._bigWinCountUp.getCountAnimation(this._fromCount, this._toCount, duration, Power1.easeInOut);
        }, 0);

        // #101
        tl.add(this.shakeCountUp(1, 1, 70, 4.5, duration * 0.4), duration * 0.6);

        return tl;
    }

    // #101, referred from SanQuentin game => AnimationEffects.Shake method
    private shakeCountUp(x: number, y: number, hertz: number, intensity: number, duration: number): TimelineMax {
        let startPosY: number,
            startPosX: number,
            t: boolean = true;
        const tl: TimelineMax = new TimelineMax({
            onStart: () => {
                startPosY = this._bigWinCountUp.y;
                startPosX = this._bigWinCountUp.x;
            },
            onComplete: () => {
                // #364
                this._bigWinCountUp.x = 360;
                this._bigWinCountUp.y = NolimitApplication.isLandscape ? 365 : 390;
            },
            onUpdate: () => {
                const lt: number = tl.time(),
                    et: number = tl.progress();

                if (t) {
                    t = false;
                    return;
                }

                if (intensity) {
                    if (y !== 0) {
                        this._bigWinCountUp.y = startPosY + Math.cos(lt * hertz) * (intensity * et) * y;
                    }
                    if (x !== 0) {
                        this._bigWinCountUp.x = startPosX + (Math.sin(lt * hertz) * (intensity * et)) * x;
                    }
                } else {
                    if (y !== 0) {
                        this._bigWinCountUp.y = startPosY + (Math.cos(lt * hertz) * et) * y;
                    }
                    if (x !== 0) {
                        this._bigWinCountUp.x = startPosX + (Math.sin(lt * hertz) * et) * x;
                    }
                }
            }
        });

        tl.to(this, duration, {});

        return tl;
    }

    private stopCountUp(calledFromAbort: boolean): TweenLite {
        // #365
        if (this._bigWinCountUpTl) {
            this._bigWinCountUpTl.pause();
            this._bigWinCountUpTl.kill();
        }

        this._bigWinCountUp.stopAt(this._toCount);
        this._bigWinCountUp.visible = true;
        this._bigWinCountUp.alpha = 1;

        this._bigWinCountUpEndTween = TweenLite.to(this._bigWinCountUp, {
            duration: 0.3,
            alpha: 0,
            delay: calledFromAbort ? 0.5 : 1.2,
            onStart: () => Mental.winWays.hideTotalWinWaysItems(0.3),
            onComplete: () => {this._bigWinCountUp.visible = false}
        });

        return this._bigWinCountUpEndTween;
    }
}